export const THEME_STORAGE_KEY = 'clinio-v2-theme';

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];

export type ResolvedTheme = 'light' | 'dark';

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function parseThemePreference(value: unknown): ThemePreference {
  return isThemePreference(value) ? value : 'system';
}

export function readStoredThemePreference(): ThemePreference {
  try {
    return parseThemePreference(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
}

export function writeStoredThemePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // localStorage not available
  }
}

export function systemPrefersDark(media: { matches: boolean } | null | undefined): boolean {
  return Boolean(media?.matches);
}

export function resolveTheme(
  preference: ThemePreference,
  media: { matches: boolean } | null | undefined,
): ResolvedTheme {
  switch (preference) {
    case 'light':
      return 'light';
    case 'dark':
      return 'dark';
    case 'system':
      return systemPrefersDark(media) ? 'dark' : 'light';
    default: {
      const _exhaustive: never = preference;
      return _exhaustive;
    }
  }
}

export function isPatientFacingPath(pathname: string): boolean {
  const path = (pathname.split('?')[0] ?? pathname).replace(/\/+$/, '') || '/';
  if (path === '/portal' || path.startsWith('/portal/')) return true;
  if (path.startsWith('/book/')) return true;
  if (path.startsWith('/pay-invoice/')) return true;
  if (path.startsWith('/accept-quote/')) return true;
  if (path === '/payment-success' || path.startsWith('/payment-success/')) return true;
  if (path === '/spoergeskemaer' || path.startsWith('/spoergeskemaer/')) return true;
  return false;
}

export function shouldApplyDarkClass(
  preference: ThemePreference,
  _pathname: string,
  media: { matches: boolean } | null | undefined,
): boolean {
  return resolveTheme(preference, media) === 'dark';
}

export function applyDarkClass(enabled: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', enabled);
}
